/*
 * Copyright (c) 2016 Omnigon Communications, LLC. All rights reserved.
 *
 * This software is the confidential and proprietary information of Omnigon Communications, LLC
 * ("Confidential Information"). You shall not disclose such Confidential Information and shall access and use it only
 * in accordance with the terms of the license agreement you entered into with Omnigon Communications, LLC, its
 * subsidiaries, affiliates or authorized licensee. Unless required by applicable law or agreed to in writing, this
 * Confidential Information is provided on an "AS IS" BASIS, WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either
 * express or implied. See the license agreement for the specific language governing permissions and limitations.
 */

import Swiper from 'swiper';
import can from 'can/util/util';
import BaseControl from 'scripts/base-control';
import carouselPreviewSlide from './carousel-preview-slide.stache';
import {Slides} from './carousel.models';
import carouselSlide from './carousel-slide.stache';
import DomEventsManager from 'scripts/dom-events-manager';
import dtm from 'scripts/services/dtm';
import _ from 'lodash';
import BrightcoveService from 'scripts/services/brightcove-service';
import appContext from 'scripts/app-context';

const ENTER_KEY_CODE = 13;

export const Carousel = BaseControl.extend({
    init () {
        this._super();
        DomEventsManager.subscribe(this);

        this.$swiperMainContainer = this.$element.find('.swiper-main-container');
        this.$swiperMainWrapper = this.$swiperMainContainer.find('.swiper-wrapper');
        this.$swiperPreviewContainer = this.$element.find('.swiper-preview-container');
        this.$swiperPreviewWrapper = this.$swiperPreviewContainer.find('.swiper-wrapper');
        this.activeVideoIndex = null;

        const filteredSlides = this.options.slides.filter(slide => {
            if (slide.type === 'video') {
                return !!slide.brightcoveId;
            }

            return true;
        });

        this.carouselSlides = new Slides(filteredSlides);

        this.slidesOptions = this.carouselSlides.map((slide, index) => {
            const options = this.getSlideOption(slide, index);
            if (options) {
                this.$swiperMainWrapper.append(carouselSlide(options));
                this.$swiperPreviewWrapper.append(carouselPreviewSlide(options));
            }

            return options;
        });

        this.initSwiper();
    },

    isMediumDown() {
        return ['medium', 'small', 'extra-small'].includes(appContext.model.screen.attr('screenSize'))
    },

    switchAutoplay(screen) {
        if (!this.swiperMain || !this.options.autoRotation) {
            return;
        }

        // const autoplayAction = this.isMediumDown() ? 'stop' : 'start';
        this.swiperMain.autoplay['start']();
    },
    
    getSlideOption (slide, index) {
        const slideType = slide.type;
        const options = _.assign({}, slide);
        let status = true;
        let image = {};
        let url = '';

        switch (slideType) {
            case 'image': {
                image = slide.image;
                if (image) {
                    url = _.get(slide, 'image.url', '');
                } else {
                    status = false;
                }

                break;
            }
            case 'video': {
                const videoPageUrl = _.get(window.globalSettings, 'pagePaths.video');

                if (options.brightcoveId) {
                    const contentImage = slide.contentImage;
                    const heroImage = slide.heroImage;

                    url = `${videoPageUrl}#brightcoveId=${options.brightcoveId}&pageTitle=${options.shortContentTitle || options.contentTitle}&playlist=unknown`;

                    if (!contentImage || !heroImage) {
                        const brightcoveImagePromise = can.Deferred();

                        BrightcoveService.getVideo(options.brightcoveId).then(data => {
                            brightcoveImagePromise.resolve({
                                type: 'imageGeneric',
                                url: data.poster
                            });
                        });

                        image = brightcoveImagePromise.promise();
                    }
                } else {
                    status = false;
                }

                break;
            }
            case 'article/photo_gallery': {
                image = slide.contentImage;

                if (!image) {
                    image = _.get(slide, 'pageCard.image');
                }

                url = _.get(slide, 'pageCard.contentUrl', _.get(slide, 'pageCard.contentPath', ''));

                options.shareUrl = _.get(slide, 'pageCard.shareUrl');
                break;
            }
        }

        options.slideNum = index;
        options.image = image;
        options.url = url;
        options.shortContentTitle = slide.shortContentTitle;

        return status ? options : false;
    },

    getMainSwiperOptions() {
        const slides = this.slidesOptions;

        const autoRotation = this.options.autoRotation;
        let rotationInterval = parseInt(this.options.autoRotationInterval, 10);

        if (!_.isInteger(rotationInterval)) {
            rotationInterval = 0;
        }

        const autoplay = autoRotation ? {delay: rotationInterval} : false;

        return {
            effect: 'fade',
            autoplay,
            speed: 1000
        };
    },

    initSwiper () {
        let options = this.getMainSwiperOptions();

        if (this.swiperMain) {
            this.swiperMain.destroy(true, true);
            this.swiperMain = null;
        }

        if (this.slidesOptions.length - this.isMediumDown() + this.options.hasSponsor > 3) {

            if (this.swiperPreview) {
                this.swiperPreview.destroy(true, true);
                this.swiperPreview = null;
            }

            this.swiperPreview = new Swiper(this.$swiperPreviewContainer, {
                direction: 'vertical',
                slidesPerView: 'auto',
                observer: true,
                mousewheel: true
            });
        }

        this.swiperMain = new Swiper(this.$swiperMainContainer, options);
        this.$swiperPreviewContainer.find('.swiper-slide:eq(0)').addClass('swiper-slide-selected');

        this.swiperMain.on('slideChange', (el) => {
            this.pauseCurrentVideo();
            this.swiperMain.autoplay.start();
        });

        this.swiperMain.on('slideChangeTransitionStart', (el, index) => {
            const currentIndexSlide = this.$swiperMainContainer.find('.swiper-slide-active').data('index');

            if (this.swiperPreview) {
                this.swiperPreview.slideTo(currentIndexSlide, 1000, false);
            }

            this.$swiperPreviewWrapper.find('.swiper-slide-selected').removeClass('swiper-slide-selected');
            this.$swiperPreviewWrapper.find('.swiper-slide:eq('+(currentIndexSlide)+')').addClass('swiper-slide-selected');
        });

        this.$swiperPreviewWrapper
            .find('.swiper-slide')
            .on('click', '.slide-preview__image a[href]', (e) => {
                // swiper by default calls preventDefault on every event for bullet
                e.stopPropagation();
            })

        const videos = this.$swiperMainContainer.find('video');
        videos.each((_index, video) => {
            $(video).removeAttr('autoplay');
        });
    },

    pauseCurrentVideo() {
        const $video = this.$swiperMainContainer.find('.video-active brightcove-video');
        if ($video.length) {
            $video.viewModel().pause();
        }
        this.$swiperMainContainer
            .find('.video-active')
            .removeClass('video-active');
        this.activeVideoIndex = null;
        this.$swiperPreviewWrapper
            .find('.slide-preview__image')
            .removeClass('slide-preview__image--playing');
    },

    playVideo($slide) {
        this.pauseCurrentVideo();
        this.swiperMain.detachEvents();
        const $container = $slide.find('.js-carousel-video-container');
        const $video = $container.find('brightcove-video');
        this.activeVideoIndex = $slide.data('index');
        this.$swiperPreviewWrapper
            .find(`.swiper-slide[data-index=${this.activeVideoIndex}]`)
            .find('.slide-preview__image')
            .addClass('slide-preview__image--playing');
        $slide.find('.js-carousel-video-slide').addClass('video-active');

        dtm.trackDeparmentContentCarouselVideoStart();
        $video.viewModel().play();
        $video.viewModel().on('ended', () => {
            dtm.trackDeparmentContentCarouselVideoComplete();
            this.swiperMain.autoplay.start();
        });
        this.swiperMain.autoplay.stop();
    },

    '.js-preview-swiper-slide keyup' ($slidePreview, event) {
        if (event.keyCode === ENTER_KEY_CODE) {
            this.swiperMain.autoplay.stop();
            this.$swiperPreviewWrapper.find('.swiper-slide-selected').removeClass('swiper-slide-selected');
            $slidePreview.addClass('swiper-slide-selected');
            const prevDataIndex = $slidePreview.data('index');
            this.swiperMain.slideTo(prevDataIndex, 1000, false);
        }
    },

    '.js-preview-swiper-slide click' ($slidePreview, event) {
        this.$swiperPreviewWrapper.find('.swiper-slide-selected').removeClass('swiper-slide-selected');
        $slidePreview.addClass('swiper-slide-selected');
        this.swiperMain.slideTo($slidePreview.data('index'), 1000, false);

        if ($(event.target).hasClass('js-play-button')) {
            const $slide = this.$swiperMainWrapper.find(`.swiper-slide[data-index=${$slidePreview.data('index')}]`);
            this.playVideo($slide);
        }
    },

    '.js-carousel-video-slide:not(.video-active) click' ($element, event) {
        const $slide = $element.parent();
        this.playVideo($slide)
    },

    '.js-play-button click' ($element, event) {
        const index = $element.parents('.js-preview-swiper-slide').data('index');
        const $slide = this.$swiperMainWrapper.find(`.swiper-slide[data-index=${index}]`);
        this.playVideo($slide);
    },

    '.swiper-preview-column .swiper-slide click': ($el) => {
        const position = parseInt($el.data('index')) + 1;
        dtm.trackDeparmentContentCarouselCardClick(position);
    },

    '.s-mobile-link click': ($el, event) => {
        event.stopImmediatePropagation();
        event.stopPropagation();
    }
});
